/* src/ui/panels/cuisinePicker.ts — the nine menus a cart can open with.
 *
 * Shown inside the cart panel once the cart is on the map and before it sells anything: a cart
 * with no menu is not a broken state, it is the state you are being asked to resolve, so it
 * takes over the panel rather than hiding behind an empty gauge.
 *
 * Every figure on a card is server-computed (`cuisine_choices()`), including the base price and
 * what a serving costs to make — the client never derives a currency value (CLAUDE.md
 * non-negotiable #2). Together those two numbers are the whole decision: the margin, and the
 * shopping list you are signing up to keep stocked.
 */

import type { CuisineChoice } from '../../core/types.ts';
import { el } from '../dom.ts';
import { formatMoney } from '../format.ts';

/** Turn `BURGER_BUN` into `burger bun` — recipe lines are read, not looked up. */
const readable = (itemId: string): string => itemId.toLowerCase().replace(/_/g, ' ');

export function renderCuisineGrid(
  cuisines: readonly CuisineChoice[],
  onPick: (cuisine: CuisineChoice) => void,
): HTMLElement {
  if (cuisines.length === 0) {
    return el('p', { class: 'gf-place-sub' }, 'Loading menus…');
  }

  return el(
    'div',
    { class: 'gf-cuisine-grid' },
    ...cuisines.map((c) =>
      el(
        'button',
        {
          class: 'gf-cuisine',
          attrs: { type: 'button' },
          on: {
            click: () => {
              onPick(c);
            },
          },
        },
        el('span', { class: 'gf-cuisine-name' }, c.name),
        el(
          'span',
          { class: 'gf-cuisine-meta' },
          `sells around ${formatMoney(c.basePrice)} · costs ${formatMoney(c.unitCost)} to make`,
        ),
        el('span', { class: 'gf-cuisine-recipe' }, c.recipe.map((r) => readable(r.itemId)).join(' · ')),
      ),
    ),
  );
}
